/**
 * auth.ts — Module utilitaire centralisé pour l'authentification.
 *
 * ⚠️ Point d'entrée UNIQUE pour :
 *   - Construction du header X-MRC-Auth
 *   - Récupération des credentials depuis le store
 *
 * Usage :
 *   import { buildAuthHeader, getAuthHeaders } from '../utils/auth'
 *
 * @version 1.0.0
 */

export interface Credentials {
  username: string
  password: string
  db?:      string
  societe?: string
}

/**
 * Retourne le token opaque d'authentification.
 * Ce token est généré et chiffré par le backend lors du login.
 */
export function buildAuthHeader(credentials: Credentials): string {
  if (!credentials?.username || !credentials?.password) {
    throw new Error('[auth] Credentials incomplets : username et password (token) requis')
  }
  // Le champ 'password' contient désormais le token opaque retourné par le backend
  return credentials.password
}

/**
 * Retourne un objet headers prêt à utiliser dans fetch/axios.
 * Retourne un objet vide si les credentials sont absents.
 */
export function getAuthHeaders(credentials: Credentials | null | undefined): Record<string, string> {
  if (!credentials?.username || !credentials?.password) return {}
  return { 'X-MRC-Auth': buildAuthHeader(credentials) }
}

/**
 * Récupère les credentials depuis le store Zustand.
 * On utilise un getter lazy pour éviter les cycles d'import
 * et les problèmes avec require() sous Vite/ESM.
 */
let _getAppState: (() => any) | null = null

export function registerAppStore(getState: () => any): void {
  _getAppState = getState
}

export function getCredentialsFromStore(): Credentials | null {
  try {
    if (_getAppState) {
      return _getAppState()?.credentials ?? null
    }
    // Fallback : import dynamique (fonctionne si le module est déjà chargé)
    const { useAppStore } = require('../store/useAppStore')
    return useAppStore.getState()?.credentials ?? null
  } catch {
    return null
  }
}

/**
 * Raccourci : récupère les headers auth depuis le store.
 */
export function getStoreAuthHeaders(): Record<string, string> {
  return getAuthHeaders(getCredentialsFromStore())
}
